import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["children", "colGutter", "rowGutter", "viewport", "colSizes", "align", "className", "bleed"];

var _defaultProps, _templateObject;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { calculateRem } from '@uie/javascript-style-helpers';
import { breakpoints } from '@vds-core/theme';
import { Grid as FlexboxGrid } from 'react-styled-flexboxgrid';
import { withVDSManager } from '@vds-core/utilities'; // ---------------------------------------------
// Props
// ---------------------------------------------

var propTypes = {
  /**
   * Children for the grid. This should be the VDS Row components,
   * containing the VDS Col components. The page content should be inside the Col components.
   */
  children: PropTypes.node.isRequired,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * Set a fixed height for the grid container
   */
  height: PropTypes.string,

  /**
   * @ignore
   */
  align: PropTypes.oneOf(['flex-start', 'space-between', 'space-around', 'flex-end', 'center', 'space-evenly']),

  /**
   * When provided, will set global sizes for all Col components for each viewport [mobile, tablet, desktop]
   */
  colSizes: PropTypes.object,

  /**
   * Set the styling of the component to the desktop, tablet, or mobile size based on passed prop.
   * Desktop and tablet are the same size.
   */
  viewport: PropTypes.oneOf(['desktop', 'tablet', 'mobile']),

  /**
   * @Ignore
   */
  colGutter: PropTypes.bool,

  /**
   * When passed, will set the global spacing between all Row components inside the grid
   */
  rowGutter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * This is the max width for the Grid container, defaults to 1272.
   */
  bleed: PropTypes.oneOf(['1440', '1272', 'full'])
};
var defaultProps = (_defaultProps = {
  className: null,
  colGutter: true,
  rowGutter: 0,
  height: '100%',
  colSizes: {
    mobile: 1,
    tablet: 1,
    desktop: 1
  },
  viewport: undefined
}, _defineProperty(_defaultProps, "rowGutter", undefined), _defineProperty(_defaultProps, "align", 'flex-start'), _defineProperty(_defaultProps, "bleed", '1272'), _defaultProps);
var baseEm = 16;
var MOBILE_GUTTER = 32;
var DESKTOP_GUTTER = 40;

function _emNumberFromBreakPoint(breakpoint) {
  var parsedBreak = Number(breakpoint.split('px', 1)),
      number = parsedBreak / baseEm;
  return number;
}

var StyledGridContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledGridContainer",
  componentId: "sc-1q2jtss-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  width: 100%;\n  max-width: ", ";\n  margin: 0 auto;\n  ", ";\n"])), function (_ref) {
  var bleed = _ref.bleed;
  return bleed === 'full' ? '100%' : bleed === '1440' ? '1440px' : '1272px';
}, function (_ref2) {
  var height = _ref2.height;
  return height && "\n    height: ".concat(height, ";\n  ");
});

function getColumnSizes(child, parentSizes, viewport) {
  var childSizes = child.props.colSizes && _mapColSizes(child.props.colSizes, viewport) || {}; // remove undefined sizes

  Object.keys(childSizes).forEach(function (key) {
    return childSizes[key] === undefined && delete childSizes[key];
  });
  if (Object.keys(childSizes).length === 4) return childSizes;else return _objectSpread(_objectSpread({}, parentSizes), childSizes);
}

function _mapColSizes(colSizes, viewport) {
  var xs = viewport ? colSizes[viewport] : colSizes.mobile,
      sm = viewport ? colSizes[viewport] : colSizes.mobile,
      md = viewport ? colSizes[viewport] : colSizes.tablet,
      lg = viewport ? colSizes[viewport] : colSizes.desktop;
  return {
    xs: xs,
    sm: sm,
    md: md,
    lg: lg
  };
}

var Grid = function Grid(props) {
  var children = props.children,
      colGutter = props.colGutter,
      rowGutter = props.rowGutter,
      viewport = props.viewport,
      colSizes = props.colSizes,
      align = props.align,
      className = props.className,
      bleed = props.bleed,
      rest = _objectWithoutProperties(props, _excluded);

  var innerWidth = window.innerWidth;
  var gutterWidth = viewport === 'mobile' || innerWidth && innerWidth < 768 ? MOBILE_GUTTER : DESKTOP_GUTTER;
  var gutterWidthRem = calculateRem(gutterWidth);

  var mappedColSizes = _mapColSizes(colSizes, viewport); // sm: '544px',
  // md: '768px',
  // lg: '991px',
  // xl: '1272px',
  // tablet is sm -> md
  // desktop is md+


  var theme = {
    flexboxgrid: {
      gridSize: viewport === 'mobile' ? 4 : 12,
      gutterWidth: colGutter ? gutterWidthRem.replace('rem', '') : 0,
      mediaQuery: 'only screen',
      breakpoints: {
        xs: _emNumberFromBreakPoint('0px'),
        //0
        sm: _emNumberFromBreakPoint(breakpoints.md),
        //0 -> 767px mobile
        md: _emNumberFromBreakPoint(breakpoints.lg),
        //768 px -> 1024px tablet
        lg: _emNumberFromBreakPoint(breakpoints.xl) //1024 - 1272 desktop

      }
    }
  };
  return /*#__PURE__*/React.createElement(FlexboxGrid, {
    theme: theme,
    fluid: true,
    style: {
      paddingRight: 0,
      paddingLeft: 0,
      marginRight: 0,
      marginLeft: 0,
      maxWidth: '100%',
      fontSize: '16px',
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(StyledGridContainer, _extends({}, rest, {
    bleed: bleed,
    theme: theme,
    gutterWidth: gutterWidth
  }), React.Children.map(children, function (child, index) {
    var styles = {
      flexDirection: 'row',
      justifyContent: align,
      marginRight: 'auto',
      marginLeft: 'auto',
      paddingRight: 0,
      paddingLeft: 0
    };
    var gutteredChildren = child && child.props && child.props.children ? React.Children.map(child.props.children, function (child, childIndex) {
      var sizes = getColumnSizes(child, mappedColSizes, viewport);
      var gutter = {
        marginBottom: rowGutter && children.length - 1 !== index ? rowGutter : 0
      };
      return /*#__PURE__*/React.cloneElement(child, _objectSpread({
        key: childIndex + 1,
        style: gutter,
        theme: theme
      }, sizes));
    }) : [];
    return /*#__PURE__*/React.cloneElement(child, {
      key: index + 1,
      style: styles,
      theme: theme
    }, gutteredChildren);
  })));
};

Grid.defaultProps = defaultProps;
Grid.propTypes = propTypes;
export default withVDSManager(Grid);